import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { GreenroomState as GS } from '../../../stores/types/types';
import Switch from '../../general-components/switch';
import { getCurrentShotIdEntity } from '../../../connection/shots';
import { AddFeatureFlagOverride, FetchFeatureFlags } from '../../../connection/utils';
import { loadMasks } from '../../../stores/actions/masks';
import { loadFontAssets } from '../../../stores/actions/font-assets';

const notReadyYet = ['audience_tab', 'audience_tab_details', 'audience_tab_engage', 'audience_tab_chat', 'audience_tab_extras'];

const labeledFlags = ['use_title_library', 'use_profile_masks', 'use_pstn'];
const flagToLabel: Record<string, string | undefined> = {
	'use_title_library': 'Stylepacks',
	'use_profile_masks': 'Profile Masks',
	'use_pstn': 'PSTN Phone Dial-Out',
}

interface FlagProps {
	flagLabel: string;
	value: boolean;
	doesMatch: boolean;
	isSmall: boolean;
	doHide: boolean;
	displayName?: string;
	callback: (flagLabel: string) => void;
}

function Flag(props: FlagProps): JSX.Element | null {
	const { flagLabel, doHide, value, doesMatch, isSmall, callback, displayName} = props;
	if (doHide) return null;
	const switchEl = <div style={{
		transform: isSmall ? 'scale(0.7)' : 'none',
		marginRight: isSmall ? 0 : '0px',
	}}><Switch
		on={value}
		onChange={() => callback(flagLabel)}
		className={`green ${doesMatch ? '' : 'pink'}`}
	/></div>;
	return <div 
		style={{
			display: 'flex',
			alignItems: 'center',
			justifyContent: isSmall ? 'unset' : 'space-between',
			marginBottom: isSmall ? '4px' : '8px',
			borderBottom: isSmall ? 'none' : '1px solid rgba(255, 255, 255, 0.1)',
		}}
	>
		{isSmall ? switchEl : null}
		<span
			className={`bl-font-${isSmall ? 'c2' : 'p1'}`}
			style={{ color: doesMatch ? 'white' : '#ff90ec', cursor: 'pointer' }}
			aria-label={flagLabel}
			onClick={() => callback(flagLabel)}
		>
			{displayName ?? flagLabel}
			{doesMatch ? ' ' : '*'}
			<span className='bl-font-c2 text-dark' style={{color: '0e0117'}}>
				{displayName && `(${flagLabel})`}
			</span>
		</span>
		{isSmall ? null : switchEl}
	</div>
}

export default function DebugFlags() {
	const dispatch = useDispatch();
	const featureFlags = useSelector((state: GS) => state.SettingsReducer.featureFlags);
	const token = useSelector((state: GS) => state.SessionReducer.token);
	const meetingId = useSelector((state: GS) => state.SessionReducer.meeting?.meeting);

	const [FFLabels, FFMap] = useMemo(() => {
		const newFFMap = new Map(Object.entries(featureFlags));
		const newFFLabels = Array.from(newFFMap.keys()).reverse();
		return [newFFLabels, newFFMap];
	}, [featureFlags]);

	const toggleFeatureFlag = async (flagLabel: string, currentValue: boolean | undefined, room: number | undefined) => {
		if (currentValue === undefined || !token || !meetingId) return;
		// Prevent turning off use_new_shot_thumbs when use_segments is ON
		if (flagLabel === 'use_new_shot_thumbs' && currentValue && FFMap.get('use_segments')) {
			return;
		}
		await AddFeatureFlagOverride(token, flagLabel, meetingId, !currentValue);
		if (flagLabel === 'use_profile_masks' && !currentValue && room) {
			dispatch(loadMasks(room));
		}
		if (flagLabel === 'use_title_library' && !currentValue && room) {
			dispatch(loadFontAssets(room));
		}
		// When use_segments is turned ON, also turn on use_new_shot_thumbs
		if (flagLabel === 'use_segments' && !currentValue) {
			const useNewShotThumbs = FFMap.get('use_new_shot_thumbs');
			if (useNewShotThumbs === false) {
				await AddFeatureFlagOverride(token, 'use_new_shot_thumbs', meetingId, true);
			}
		}
	};

	const [remoteFFs, setRemoteFFs] = useState<Map<string, boolean>>(FFMap);
	const updateRemoteFFs = async () => {
		const envsFlags = await FetchFeatureFlags();
		setRemoteFFs(new Map(Object.entries(envsFlags)));
	};

	useEffect(() => {
		updateRemoteFFs();
	}, [FFLabels, FFMap]);

	return (<div className="section settings">
		<h5>Feature Flags</h5>
		<div style={{ maxWidth: '100%', marginBottom: '8px' }}>
			<div className='bl-font-s1'>Toggle feature flag overrides for this room.</div>
			<div className='bl-font-s3'>Will not impact environment-level feature flags.</div>
			<div className='bl-font-s3'>Flags that do not match the environment display in pink.</div>
		</div>
		<div className="advanced-settings-container">
			{labeledFlags.map((flagLabel: string) => <Flag
				key={flagLabel}
				flagLabel={flagLabel}
				displayName={flagToLabel[flagLabel]}
				isSmall={false}
				doHide={false}
				value={FFMap.get(flagLabel) ?? false}
				doesMatch={remoteFFs.get(flagLabel) === FFMap.get(flagLabel)}
				callback={(label) => toggleFeatureFlag(label, FFMap.get(label), meetingId)}
			/>)}
		</div>
		<div className="advanced-settings-container">
			{FFLabels.map((flagLabel: string) => <Flag
				key={flagLabel}
				flagLabel={flagLabel}
				isSmall={false}
				doHide={notReadyYet.includes(flagLabel) || labeledFlags.includes(flagLabel)}
				value={FFMap.get(flagLabel) ?? false}
				doesMatch={remoteFFs.get(flagLabel) === FFMap.get(flagLabel)}
				callback={(label) => toggleFeatureFlag(label, FFMap.get(label), meetingId)}
			/>)}
		</div>
		<div style={{ marginTop: '24px', opacity: 0.8 }}>
			<div className='bl-font-c1 margin-bottom-small'>Audience Link Feature:</div>
			{notReadyYet.map((flagLabel: string) => <Flag
				key={flagLabel}
				flagLabel={flagToLabel[flagLabel] ?? flagLabel}
				isSmall={true}
				doHide={false}
				value={FFMap.get(flagLabel) ?? false}
				doesMatch={remoteFFs.get(flagLabel) === FFMap.get(flagLabel)}
				callback={(label) => toggleFeatureFlag(label, FFMap.get(label), meetingId)}
			/>)}
		</div>
	</div>);
}
